/**
 * Captura de áudio + transcrição local via Whisper.js (com Web Speech como
 * fallback) + detecção heurística de idioma.
 *
 * Pipeline:
 *   1. MediaRecorder grava o blob.
 *   2. Web Speech API (Chrome) opcionalmente roda em paralelo, alimentando
 *      `onTranscriptUpdate` com texto interim e setando `lang` heurístico.
 *      Útil porque dá preview enquanto Whisper ainda nem começou.
 *   3. Quando o usuário para a gravação:
 *      - Resolvemos o blob imediatamente.
 *      - Dispara whisper.transcribeBlob(blob) em paralelo. Quando termina,
 *        SUBSTITUI o texto da Web Speech (Whisper costuma ser mais preciso).
 *      - Se Whisper falhar (modelo não baixou, browser sem WebAssembly),
 *        fica com o texto da Web Speech — ou string vazia.
 */

import { transcribeBlob, whisperSupported } from "./whisper.js";
import { recursoAtivo } from "./inferencia.js";

export interface RecordResult {
  blob: Blob;
  mime: string;
  durationMs: number;
  /** Resolve quando o reconhecedor terminar (ou imediatamente, se ausente).
   *  Permite enviar o áudio antes da transcrição estar pronta. */
  transcription: Promise<{ text: string; lang: string }>;
}

type AnyWindow = typeof window & {
  SpeechRecognition?: new () => SpeechRecognitionLike;
  webkitSpeechRecognition?: new () => SpeechRecognitionLike;
};

interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  onresult: ((ev: SpeechResultLike) => void) | null;
  onerror: ((ev: unknown) => void) | null;
  onend: (() => void) | null;
  start: () => void;
  stop: () => void;
  abort: () => void;
}

interface SpeechResultLike {
  resultIndex: number;
  results: ArrayLike<{ 0: { transcript: string }; isFinal: boolean }>;
}

export class AudioRecorder {
  private rec: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private startTs = 0;
  private speechRec: SpeechRecognitionLike | null = null;
  private stream: MediaStream | null = null;
  private startLang = "";
  private contextHint = "";
  private finalText = "";
  private interimText = "";
  private onTranscriptUpdate: (text: string) => void;

  constructor(onTranscriptUpdate?: (text: string) => void) {
    this.onTranscriptUpdate = onTranscriptUpdate ?? (() => {});
  }

  /** Último erro da Web Speech, para diagnóstico na tela de inferências.
   *  Os comuns: "network" (Google inacessível), "not-allowed" (sem permissão
   *  de microfone), "service-not-allowed" (o app não tem acesso ao serviço). */
  static ultimoErroFala: string | null = null;

  /** Diagnóstico da última GRAVAÇÃO (não do botão de teste).
   *
   *  Guardado em disco de propósito: o defeito só aparece ao gravar, e entre
   *  gravar e ir olhar a tela o app pode ter sido fechado. Sem persistir, a
   *  informação que interessa se perde justamente no caso que interessa. */
  private static CHAVE_DIAG = "wuwei.diagGravacao";

  static registrarFalha(erro: string): void {
    try {
      localStorage.setItem(
        AudioRecorder.CHAVE_DIAG,
        JSON.stringify({ erro, mime: AudioRecorder.ultimoMime, quando: Date.now() }),
      );
    } catch {
      // sem persistência: o diagnóstico se perde, mas nada quebra
    }
  }

  static registrarSucesso(): void {
    try {
      localStorage.removeItem(AudioRecorder.CHAVE_DIAG);
    } catch {
      // idem
    }
  }

  static lerDiagnostico(): { erro: string; mime: string; quando: number } | null {
    try {
      const cru = localStorage.getItem(AudioRecorder.CHAVE_DIAG);
      return cru ? JSON.parse(cru) : null;
    } catch {
      return null;
    }
  }

  /** Formato que este aparelho realmente usa para gravar — entra no
   *  diagnóstico porque é a diferença entre o teste e a gravação real. */
  static ultimoMime = "";

  static supported(): boolean {
    return (
      typeof MediaRecorder !== "undefined" &&
      typeof navigator !== "undefined" &&
      !!navigator.mediaDevices?.getUserMedia
    );
  }

  /** True se algum mecanismo de transcrição está disponível: Whisper.js
   *  funciona em qualquer browser moderno, Web Speech só em Chrome. */
  static speechSupported(): boolean {
    if (whisperSupported()) return true;
    if (!recursoAtivo("transcricaoGoogle")) return false;
    const w = window as AnyWindow;
    return !!(w.SpeechRecognition || w.webkitSpeechRecognition);
  }

  isRecording(): boolean {
    return this.rec !== null && this.rec.state === "recording";
  }

  elapsedMs(): number {
    return this.startTs ? Date.now() - this.startTs : 0;
  }

  async start(lang?: string, contextHint?: string): Promise<void> {
    if (this.isRecording()) return;
    this.startLang = lang || navigator.language || "pt-BR";
    this.contextHint = contextHint || "";
    this.chunks = [];
    this.finalText = "";
    this.interimText = "";

    this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mimes = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/ogg;codecs=opus",
      "audio/mp4",
    ];
    const mime = mimes.find((m) => MediaRecorder.isTypeSupported(m)) ?? "";
    this.rec = mime ? new MediaRecorder(this.stream, { mimeType: mime }) : new MediaRecorder(this.stream);
    this.rec.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) this.chunks.push(e.data);
    };
    this.startTs = Date.now();
    AudioRecorder.ultimoErroFala = null;
    this.rec.start();

    // Web Speech API roda na nuvem do Google no Chrome. Só entra como
    // fallback se o Whisper local não estiver disponível E o usuário tiver
    // permitido explicitamente — mandar áudio pra fora é escolha dele, não
    // consequência automática de desligar a transcrição local.
    const useWebSpeechFallback =
      !whisperSupported() && recursoAtivo("transcricaoGoogle");
    if (useWebSpeechFallback) {
      const w = window as AnyWindow;
      const SR = w.SpeechRecognition || w.webkitSpeechRecognition;
      if (SR) {
        const sr = new SR();
        sr.lang = this.startLang;
        sr.continuous = true;
        sr.interimResults = true;
        sr.onresult = (ev) => {
          let interim = "";
          let final = this.finalText;
          for (let i = ev.resultIndex; i < ev.results.length; i++) {
            const r = ev.results[i];
            const t = r[0].transcript;
            if (r.isFinal) final += t;
            else interim += t;
          }
          this.finalText = final;
          this.interimText = interim;
          this.onTranscriptUpdate((final + interim).trim());
        };
        sr.onerror = (ev: unknown) => {
          const codigo = (ev as { error?: string } | null)?.error;
          // Antes isto era engolido em silêncio, e o resultado era uma
          // transcrição que simplesmente não vinha, sem ninguém saber por quê.
          // Guardamos o motivo para a tela de inferências poder mostrá-lo.
          AudioRecorder.ultimoErroFala = String(codigo || "desconhecido");
        };
        sr.onend = () => {
          /* nada — já tratamos via stop() */
        };
        try {
          sr.start();
          this.speechRec = sr;
        } catch {
          this.speechRec = null;
        }
      }
    }
  }

  async stop(): Promise<RecordResult> {
    return new Promise<RecordResult>((resolve, reject) => {
      const rec = this.rec;
      if (!rec) {
        reject(new Error("not recording"));
        return;
      }
      const sr = this.speechRec;
      const startLang = this.startLang;
      const contextHint = this.contextHint;

      // 1) Coleta o que a Web Speech tem (preview rápido).
      const webSpeechResult = new Promise<{ text: string; lang: string }>((resolveT) => {
        if (!sr) {
          resolveT({ text: "", lang: "" });
          return;
        }
        let done = false;
        const settle = () => {
          if (done) return;
          done = true;
          const text = (this.finalText + " " + this.interimText).trim();
          const lang = detectLang(text) || startLang;
          resolveT({ text, lang });
        };
        sr.onend = settle;
        // Failsafe: se o reconhecedor demorar, fechamos com o que temos.
        setTimeout(settle, 6000);
        try {
          sr.stop();
        } catch {
          settle();
        }
      });

      rec.onstop = () => {
        const blob = new Blob(this.chunks, { type: rec.mimeType || "audio/webm" });
        AudioRecorder.ultimoMime = blob.type;
        // 2) Whisper em paralelo. Se ele entregar (eventualmente), usamos.
        //    Se falhar/demorar demais, caímos no resultado da Web Speech.
        const whisperResult: Promise<{ text: string; lang: string }> = whisperSupported()
          ? transcribeBlob(blob, { langHint: startLang, contextHint })
              .then((r) => {
                AudioRecorder.registrarSucesso();
                const lang = detectLang(r.text) || r.lang || startLang;
                return { text: r.text, lang };
              })
              .catch((e) => {
                // Este catch engolia o erro em silêncio — e era por isso que o
                // áudio voltava sem texto sem ninguém saber por quê. O caminho
                // do botão de teste usa WAV e funciona; a gravação usa
                // webm/opus, que nem todo aparelho decodifica. Guardamos o
                // motivo para a tela de inferências poder mostrá-lo.
                AudioRecorder.registrarFalha(
                  String((e as Error)?.message || e).slice(0, 160),
                );
                return { text: "", lang: "" };
              })
          : Promise.resolve({ text: "", lang: "" });

        // 3) Combina: Whisper vence se tiver texto; senão usa Web Speech.
        const transcription = (async () => {
          const w = await whisperResult;
          if (w.text) return w;
          return webSpeechResult;
        })();

        resolve({
          blob,
          mime: blob.type,
          durationMs: Date.now() - this.startTs,
          transcription,
        });
        // Limpa stream/MediaRecorder; a SpeechRecognition é gerida pela Promise acima.
        if (this.stream) {
          this.stream.getTracks().forEach((t) => t.stop());
          this.stream = null;
        }
        this.rec = null;
        this.speechRec = null;
        this.startTs = 0;
      };
      try {
        rec.stop();
      } catch (e) {
        reject(e);
      }
    });
  }

  cancel(): void {
    if (this.rec && this.rec.state !== "inactive") {
      this.rec.onstop = null;
      try {
        this.rec.stop();
      } catch {
        /* */
      }
    }
    if (this.speechRec) {
      try {
        this.speechRec.abort();
      } catch {
        /* */
      }
    }
    this.cleanup();
  }

  private cleanup(): void {
    if (this.stream) {
      this.stream.getTracks().forEach((t) => t.stop());
      this.stream = null;
    }
    this.rec = null;
    this.speechRec = null;
    this.startTs = 0;
    this.contextHint = "";
  }
}

/**
 * Detecta idioma a partir do texto. Heurística: dicionário de palavras
 * comuns + bônus por caracteres específicos. Retorna "" se confiança
 * for muito baixa (texto vazio ou inconclusivo).
 */
export function detectLang(text: string): string {
  if (!text) return "";
  const t = " " + text.toLowerCase() + " ";
  if (t.replace(/\s+/g, " ").length < 4) return "";

  const lex: Record<string, string[]> = {
    "pt-BR": [
      " o ", " a ", " de ", " que ", " e ", " do ", " da ", " em ", " um ",
      " para ", " com ", " não ", " uma ", " os ", " no ", " se ", " na ",
      " por ", " mais ", " você ", " eu ", " ele ", " ela ", " está ", " sim ",
      " também ", " muito ", " tudo ", " bom ", " olá ", " oi ", " obrigado ",
    ],
    "en-US": [
      " the ", " a ", " of ", " and ", " to ", " in ", " is ", " you ",
      " that ", " it ", " he ", " was ", " for ", " on ", " are ", " with ",
      " as ", " i ", " his ", " they ", " yes ", " no ", " hello ", " hi ",
      " thanks ", " good ", " all ",
    ],
    "es-ES": [
      " el ", " la ", " de ", " que ", " y ", " a ", " en ", " un ", " no ",
      " por ", " con ", " su ", " para ", " como ", " ser ", " estar ",
      " tener ", " hay ", " sí ", " hola ", " gracias ", " bueno ", " todo ",
    ],
    "fr-FR": [
      " le ", " la ", " de ", " et ", " à ", " un ", " être ", " en ",
      " avoir ", " que ", " pour ", " dans ", " ce ", " il ", " qui ", " ne ",
      " sur ", " se ", " pas ", " une ", " du ", " est ", " oui ", " bonjour ",
      " merci ", " bon ", " tout ",
    ],
  };

  const scores: Record<string, number> = {
    "pt-BR": 0,
    "en-US": 0,
    "es-ES": 0,
    "fr-FR": 0,
  };

  for (const [lang, words] of Object.entries(lex)) {
    for (const w of words) {
      let i = 0;
      while ((i = t.indexOf(w, i)) >= 0) {
        scores[lang]!++;
        i += w.length;
      }
    }
  }

  if (/[ãõçâê]/.test(t)) scores["pt-BR"]! += 3;
  if (/ção|ões|inho/.test(t)) scores["pt-BR"]! += 4;
  if (/[ñ¿¡]/.test(t)) scores["es-ES"]! += 5;
  if (/ción|hola/.test(t)) scores["es-ES"]! += 2;
  if (/[œëâôûÿ]|\bc'|\bd'|\bj'|\bn'|\bl'|\bs'/.test(t)) scores["fr-FR"]! += 3;

  let best = "";
  let bestScore = 0;
  for (const [l, s] of Object.entries(scores)) {
    if (s > bestScore) {
      best = l;
      bestScore = s;
    }
  }
  return bestScore >= 1 ? best : "";
}

export async function blobToBase64(b: Blob): Promise<string> {
  const buf = await b.arrayBuffer();
  const u = new Uint8Array(buf);
  let bin = "";
  const chunk = 0x8000;
  for (let i = 0; i < u.length; i += chunk) {
    bin += String.fromCharCode(...u.subarray(i, i + chunk));
  }
  return btoa(bin);
}

export function base64ToBlob(b64: string, mime: string): Blob {
  const bin = atob(b64);
  const u = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) u[i] = bin.charCodeAt(i);
  return new Blob([u], { type: mime });
}

export function formatDuration(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  const m = Math.floor(s / 60);
  const ss = (s % 60).toString().padStart(2, "0");
  return `${m}:${ss}`;
}

export function shortLang(lang: string): string {
  if (!lang) return "?";
  const m = lang.match(/^([a-z]{2})(?:-([A-Z]{2}))?/i);
  if (!m) return lang;
  return m[2] ? `${m[1]!.toUpperCase()}-${m[2]}` : m[1]!.toUpperCase();
}
